import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "@chakra-ui/react";
import VoiceTypingPopup from "./VoiceTypingPopup";

/**
 * VoiceTyping Component
 *
 * Uses the browser's built-in Web Speech API (window.SpeechRecognition /
 * webkitSpeechRecognition) — the same free Google speech-to-text path the
 * AudioRecorder live-transcription fallback uses. Available to all roles
 * because no backend transcription credentials are required.
 */
const VoiceTyping = ({
  onTranscriptionResult,
  isEnabled = true,
  className = "",
  size = "md",
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const isActiveRef = useRef(false);
  const shouldKeepRunningRef = useRef(false);
  const networkErrorCountRef = useRef(0);
  const toast = useToast();

  const cleanup = useCallback(() => {
    console.log("🎤 VoiceTyping: Cleaning up resources...");
    shouldKeepRunningRef.current = false;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onresult = null; } catch (_) {}
      try { rec.onerror = null; } catch (_) {}
      try { rec.onend = null; } catch (_) {}
      try { rec.onstart = null; } catch (_) {}
      if (isActiveRef.current) {
        try { rec.stop(); } catch (_) {}
      }
    }
    recognitionRef.current = null;
    isActiveRef.current = false;
    networkErrorCountRef.current = 0;
    setIsRecording(false);
    setIsConnecting(false);
    // Commit any in-flight interim preview as-is — the consumer should drop
    // its tracking ref but leave the rendered text in place.
    if (onTranscriptionResult) {
      try { onTranscriptionResult("", { isFinal: false, keepInterim: true }); } catch (_) {}
    }
  }, [onTranscriptionResult]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const startVoiceTyping = useCallback(async () => {
    try {
      console.log("🎤 VoiceTyping: Starting voice typing via Web Speech API...");

      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        throw new Error(
          "Live transcription only works on Chromium-based browsers. Please use Chrome, Comet, or Edge."
        );
      }

      setIsConnecting(true);
      setError(null);
      networkErrorCountRef.current = 0;

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        console.log("🎤 VoiceTyping: Recognition started");
        isActiveRef.current = true;
        setIsConnecting(false);
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        networkErrorCountRef.current = 0;
        let interim = "";
        const finals = [];
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const segment = result[0]?.transcript || "";
          if (result.isFinal) {
            const trimmed = segment.trim();
            if (trimmed) finals.push(trimmed);
          } else {
            interim += segment;
          }
        }
        if (!onTranscriptionResult) return;
        finals.forEach((text) => {
          console.log("🎤 VoiceTyping: Final segment:", text);
          onTranscriptionResult(text, { isFinal: true });
        });
        // Always emit interim (even empty) so the consumer can clear any
        // pending preview when an utterance has just been finalized.
        onTranscriptionResult(interim, { isFinal: false });
      };

      recognition.onerror = (event) => {
        console.error("🎤 VoiceTyping: Recognition error:", event.error);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          shouldKeepRunningRef.current = false;
          setError("Microphone permission denied for speech recognition.");
        } else if (event.error === "network") {
          networkErrorCountRef.current += 1;
          if (networkErrorCountRef.current >= 3) {
            shouldKeepRunningRef.current = false;
            const isBrave = typeof navigator !== "undefined" && navigator.brave;
            setError(
              isBrave
                ? "Live transcription does not work on Brave — Brave blocks the speech recognition network connection. Please use Chrome, Comet, or Edge."
                : "Live transcription is unavailable. Please use a Chromium-based browser like Chrome, Comet, or Edge."
            );
          }
        } else if (event.error === "no-speech" || event.error === "aborted") {
          // benign — onend will auto-restart when shouldKeepRunningRef is true
        } else {
          setError(`Speech recognition error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        console.log(
          "🎤 VoiceTyping: Recognition ended — keepRunning:",
          shouldKeepRunningRef.current
        );
        isActiveRef.current = false;
        // Chrome fires onend after every final result even with continuous=true.
        // Auto-restart so dictation continues across pauses.
        if (shouldKeepRunningRef.current) {
          setTimeout(() => {
            if (!shouldKeepRunningRef.current) return;
            const r = recognitionRef.current;
            if (!r || isActiveRef.current) return;
            try {
              r.start();
            } catch (err) {
              console.warn(
                "🎤 VoiceTyping: Could not restart:",
                err.name,
                err.message
              );
            }
          }, 150);
        } else {
          setIsRecording(false);
          setIsConnecting(false);
        }
      };

      recognitionRef.current = recognition;
      shouldKeepRunningRef.current = true;
      recognition.start();
    } catch (err) {
      console.error("🎤 VoiceTyping: Error starting voice typing:", err);
      setError(err.message);
      setIsConnecting(false);
      cleanup();

      toast({
        title: "Voice Typing Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast, onTranscriptionResult, cleanup]);

  const stopVoiceTyping = useCallback(() => {
    console.log("🎤 VoiceTyping: Stopping voice typing...");
    cleanup();
  }, [cleanup]);

  const handleToolbarClick = useCallback(() => {
    if (isRecording) {
      stopVoiceTyping();
    } else {
      setShowPopup(true);
    }
  }, [isRecording, stopVoiceTyping]);

  const handlePopupStartRecording = useCallback(() => {
    startVoiceTyping();
  }, [startVoiceTyping]);

  const handlePopupStopRecording = useCallback(() => {
    stopVoiceTyping();
  }, [stopVoiceTyping]);

  const handlePopupClose = useCallback(() => {
    setShowPopup(false);
    if (isRecording) {
      stopVoiceTyping();
    }
  }, [isRecording, stopVoiceTyping]);

  const getButtonState = () => {
    if (isConnecting) {
      return { icon: faMicrophone, color: "orange", text: "Connecting..." };
    } else if (isRecording) {
      return { icon: faStop, color: "red", text: "Stop" };
    } else {
      return { icon: faMicrophone, color: "blue", text: "Voice Type" };
    }
  };

  const buttonState = getButtonState();

  if (!isEnabled) {
    return null;
  }

  return (
    <div className={`voice-typing ${className}`}>
      <button
        onClick={handleToolbarClick}
        disabled={isConnecting}
        className={`voice-typing-button ${isRecording ? "recording" : ""} ${
          isConnecting ? "connecting" : ""
        }`}
        style={{
          padding:
            size === "sm"
              ? "4px 4px"
              : size === "lg"
              ? "12px 16px"
              : "8px 12px",
          fontSize: size === "sm" ? "12px" : size === "lg" ? "16px" : "14px",
          minWidth: size === "sm" ? "auto" : undefined,
          width: size === "sm" ? "auto" : undefined,
        }}
        title={buttonState.text}
      >
        <FontAwesomeIcon
          icon={buttonState.icon}
          className={isRecording ? "pulse" : ""}
          style={{
            animation: isRecording ? "pulse 1s infinite" : "none",
          }}
        />
        {size !== "sm" && <span>{buttonState.text}</span>}
      </button>

      {error && (
        <div
          style={{
            color: "red",
            fontSize: "12px",
            marginTop: "4px",
          }}
        >
          {error}
        </div>
      )}

      <VoiceTypingPopup
        isOpen={showPopup}
        onClose={handlePopupClose}
        onStartRecording={handlePopupStartRecording}
        onStopRecording={handlePopupStopRecording}
        isRecording={isRecording}
        isConnecting={isConnecting}
        error={error}
      />
    </div>
  );
};

export default VoiceTyping;
